import type { OnConsentChangedPayload, OnConsentSetPayload } from 'c15t';

import { tags } from './tags.js';

/**
 * Consent-driven tag container — fan out c15t lifecycle events and validated
 * `track()` calls across every registered third-party tag. Wired into
 * `<ConsentProvider>` callbacks + `track()`'s dispatcher in
 * [`main.tsx`](../../spa/main.tsx) and [`analytics/track.ts`](../analytics/track.ts).
 *
 * Conceptually mirrors a GTM container: the CMP plumbing (consent state +
 * banner + dialog) lives in `consent/`, the per-tag opt-in/out + identity +
 * capture logic lives next to each vendor's SDK helpers, and this module is
 * the declarative registry that wires them together. The provider and
 * `track()` never reference vendors by name. Adding a new tag is a single-
 * file change in [`tags.ts`](./tags.ts).
 *
 * Throwing tags propagate — we surface bugs rather than swallow them. If a
 * future tag genuinely needs per-tag isolation, add a try/catch loop here
 * AND a test that covers it.
 */

export function setupTagsFromConsent(payload: OnConsentSetPayload): void {
  for (const tag of tags) tag.onSet?.(payload);
}

export function syncTagsToConsent(payload: OnConsentChangedPayload): void {
  for (const tag of tags) tag.onChange?.(payload);
}

export function dispatchTrackToTags(event: string, data: Record<string, unknown>): void {
  for (const tag of tags) tag.onTrack?.(event, data);
}
